import { inject, ChangeDetectionStrategy, ChangeDetectorRef, signal, Component, input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';
import { LoggerService } from '@/core/services/logger.service';

import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, FormsModule } from '@angular/forms';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { MessageService } from 'primeng/api';
import { PcmsoFeatureService } from './pcmso.service';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PcmsoCreateRequest, PcmsoRequest, PcmsoUpdateRequest } from '@/core/api/sicom-api/registro/medico/pcmso/pcmso.model';
import { MenuItem } from 'primeng/api';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { DateUtils } from '@/shared/utils/date-utils';
import { TagModule } from 'primeng/tag';
import { PageResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';

interface EmpresaCadastrada {
    razaoSocialNome: string;
    divisaoAreaSetor: string;
    cnpjCpf: string;
    dataInicio: string;
    dataFim: string;
    indiceRegistro: number;
}

interface EmpresaCadastradaTabela extends EmpresaCadastrada {
    situacao: { value: string; color: string };
}

import { TabContainer, TabContent, TabContainerOptionType } from '@/shared/components/layout/tab-container/tab-container';

@Component({
    selector: 'app-pcmso',
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, CardContent, Button, InputText, InputMask, Select, Tooltip, BotaoAcoes, CustomTableComponent, TagModule, TabContainer, TabContent, CanDirective],
    templateUrl: './pcmso.html',
    styleUrl: './pcmso.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Aba de PCMSO: lista/edita registros por pessoa (abas ativas/inativas). */
export class Pcmso implements OnInit, OnChanges {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    protected readonly rolePcmsoEditar = PERMISSOES.medicos.pcmso.editar;
    private readonly fb = inject(FormBuilder);
    readonly pcmsoForm = this.fb.group({
        // Dados PCMSO
        tipoRegistro: ['PJ', Validators.required],
        numeroRegistro: [''],
        cnpj: ['', [Validators.required, this.cnpjValidoValidator()]],
        razaoSocial: ['', Validators.required],
        emailEmpresa: ['', [Validators.required, this.emailValidoValidator()]],
        divisaoAreaSetor: [''],
        nrFuncionarios: this.fb.control<string | number | null>('', [Validators.min(0)]),
        classificacaoRisco: this.fb.control<string | number | null>(''),

        // Coordenação
        coordenacaoDataInicio: ['', [Validators.required, this.dataMaiorQueHojeValidator()]],
        coordenacaoDataFim: [''],

        // Documentação Comprobatória
        documentacaoDescricao: [''],

        // Prontuário no DMD
        prontuarioDmdUrl: ['']
    });
    modoEdicao: boolean = false;
    // Diferencia "novo" (criação) de "editar"
    modoCriacao: boolean = false;
    mostrarFormulario: boolean = false;
    private readonly cdr = inject(ChangeDetectorRef);

    // Loading/Erro
    loading = signal(false);
    erro?: string;

    // Aba selecionada: 'ATIVAS' | 'INATIVAS'
    abaSelecionada: 'ATIVAS' | 'INATIVAS' = 'ATIVAS';

    // Opções para seleção de aba
    optionsTab: TabContainerOptionType[] = [
        { label: 'Ativas', value: 'ATIVAS' },
        { label: 'Inativas', value: 'INATIVAS' }
    ];

    // Opções para Tipo de Registro (substituindo radio buttons por p-select)
    tipoRegistroOptions = [
        { label: 'Pessoa Jurídica', value: 'PJ' },
        { label: 'Pessoa Física', value: 'PF' }
    ];

    // Opções para Classificação de Risco (carregadas da API)
    classificacaoRiscoOptions = signal<{ label: string; value: number }[]>([]);

    // Dados para empresas cadastradas
    empresasAtivas: EmpresaCadastrada[] = [];
    empresasInativas: EmpresaCadastrada[] = [];

    // Navegação entre registros PCMSO
    registrosPcmso: PcmsoRequest[] = [];
    private indiceAtual: number = 0;

    // Paginação
    first = 0;
    rows = 10;
    totalRecords = 0;

    // Anexo PDF (padrão Biometria): arquivo selecionado e nome
    arquivoPdf: File | null = null;
    arquivoPdfNome: string = '';

    // Substituição de anexo por registro
    substituicaoPdf: File | null = null;
    substituicaoPdfNome: string = '';
    acoesNovo: MenuItem[] = [
        {
            label: 'Novo',
            icon: 'pi pi-plus',
            styleClass: 'bg-green-500',
            command: () => this.abrirDialogNovo()
        }
    ];
    acoesFormulario: MenuItem[] = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            styleClass: 'bg-blue-500',
            command: () => this.habilitarEdicao()
        },
        {
            label: 'Remover',
            icon: 'pi pi-trash',
            styleClass: 'bg-red-500',
            command: () => this.deletar()
        }
    ];

    get empresasTableMetadata(): TableMetadata {
        return {
            headers: {
                razaoSocialNome: { type: 'text', default: '-', hidden: false, value: 'Razão Social / Nome' },
                divisaoAreaSetor: { type: 'text', default: '-', hidden: false, value: 'Divisão / Área / Setor' },
                cnpjCpf: { type: 'text', default: '-', hidden: false, value: 'CNPJ / CPF' },
                dataInicio: { type: 'text', default: '-', hidden: false, value: 'Data Início' },
                dataFim: { type: 'text', default: '-', hidden: false, value: 'Data Fim' },
                situacao: { type: 'tag', default: '-', hidden: false, value: 'Situação' }
            },
            title: 'razaoSocialNome',
            tag: 'situacao',
            actions:
                this.abaSelecionada === 'ATIVAS'
                    ? {
                          visualizar: {
                              label: 'Visualizar',
                              icon: 'pi pi-eye',
                              color: 'success',
                              command: (item: EmpresaCadastradaTabela) => this.abrirDialogRegistro(item)
                          }
                      }
                    : undefined
        };
    }

    obterRazaoSocialExibicao(item: { razaoSocialNome?: string }): string {
        const r = item.razaoSocialNome || '';
        const limit = r.length > 70 ? r.substring(0, 70) + '...' : r;
        return limit.toUpperCase();
    }

    constructor(
        private pcmsoFeature: PcmsoFeatureService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.inicializarFormulario();
        // Carrega classificações de risco (não requer parâmetros)
        this.carregarRiscos();
        const p = this.pessoa();
        if (p && p.id != null && !!p.uf) {
            this.carregarDados(p);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p && p.id != null && !!p.uf) {
                this.first = 0;
                this.carregarDados(p);
            } else {
                this.empresasAtivas = [];
                this.empresasInativas = [];
                this.totalRecords = 0;
            }
        }
    }

    // CPF/CNPJ, Razão Social/Nome, E-mail, Data Início e o Documento Comprobatório
    inicializarFormulario(): void {
        this.pcmsoForm.addValidators(() => {
            if (this.pdfValid()) return null;
            else return { invalidPdf: true };
        });
        this.pcmsoForm.addValidators(this.periodoCoordenacaoValidator());
    }

    private cnpjValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return FormularioValidatorUtil.cnpjValido(valor) ? null : { cnpjInvalido: true };
        };
    }

    private emailValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return FormularioValidatorUtil.emailValido(valor) ? null : { emailInvalido: true };
        };
    }

    private dataMaiorQueHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? { dataMaiorQueHojeInvalida: true } : null;
        };
    }

    private periodoCoordenacaoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const dataInicio = String(control.get('coordenacaoDataInicio')?.value ?? '').trim();
            const dataFim = String(control.get('coordenacaoDataFim')?.value ?? '').trim();

            if (!dataInicio || !dataFim) return null;
            if (!DateUtils.isDataValida(dataInicio) || !DateUtils.isDataValida(dataFim)) return null;

            const inicio = this.converterDataBr(dataInicio);
            const fim = this.converterDataBr(dataFim);

            if (!inicio || !fim) return null;

            return inicio.getTime() > fim.getTime() ? { coordenacaoPeriodoInvalido: true } : null;
        };
    }

    private converterDataBr(valor: string): Date | null {
        const [dia, mes, ano] = valor.split('/').map((parte) => Number(parte));

        if (!dia || !mes || !ano) return null;

        return new Date(ano, mes - 1, dia);
    }

    private carregarDados(pessoa: Pessoa): void {
        this.loading.set(true);
        this.erro = undefined;
        const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
        const size = this.rows;

        const obs = this.abaSelecionada === 'ATIVAS' ? this.pcmsoFeature.buscar({ id: pessoa.id, uf: pessoa.uf, page, size }) : this.pcmsoFeature.buscarInativo({ id: pessoa.id, uf: pessoa.uf, page, size });

        obs.subscribe({
            next: (resp: PageResponse<PcmsoRequest>) => {
                this.totalRecords = resp?.totalElements ?? 0;
                // Armazena a lista completa para o navegador de registros
                const lista = (resp?.content || []).slice();
                // Ordena por dtEntrada desc (mais recente primeiro)
                const parseDt = (v?: string | null) => (v ? new Date(v).getTime() : 0);
                lista.sort((a, b) => parseDt(b.dtEntrada) - parseDt(a.dtEntrada));
                this.registrosPcmso = lista;
                this.indiceAtual = 0;
                if (this.registrosPcmso.length > 0) {
                    this.renderizarRegistroAtual();
                } else {
                    this.limparFormularioSemRegistros();
                }

                const ativas: EmpresaCadastrada[] = [];
                const inativas: EmpresaCadastrada[] = [];
                lista.forEach((r, idx) => {
                    const row: EmpresaCadastrada = {
                        razaoSocialNome: r.nmEmpresa ?? '',
                        divisaoAreaSetor: r.dsArea ?? '',
                        cnpjCpf: r.nuCnpj ?? '',
                        dataInicio: this.formatarData(r.dtEntrada),
                        dataFim: this.formatarData(r.dtSaida),
                        indiceRegistro: idx
                    };
                    const situacao = (r.inSituacao || '').toUpperCase();
                    if (situacao === 'ATIVA') ativas.push(row);
                    else if (situacao === 'INATIVA' || r.dtSaida) inativas.push(row);
                    else ativas.push(row);
                });
                this.empresasAtivas = ativas;
                this.empresasInativas = inativas;
            },
            error: (err: Error) => {
                this.logger.error('Erro ao buscar PCMSO', err);
                this.erro = err.message || 'Falha ao carregar informações de PCMSO.';
                this.showToast('error', 'Erro ao buscar PCMSO', this.erro);
                this.empresasAtivas = [];
                this.empresasInativas = [];
                this.registrosPcmso = [];
                this.indiceAtual = 0;
                this.totalRecords = 0;
            }
        }).add(() => {
            this.loading.set(false);
        });
    }

    private carregarRiscos(): void {
        this.pcmsoFeature.listarRiscos().subscribe({
            next: (lista) => {
                this.classificacaoRiscoOptions.set(
                    (lista || []).map((r) => ({
                        label: r.dsRisco,
                        value: r.id
                    }))
                );
            },
            error: (err: unknown) => {
                this.logger.error('Erro ao carregar classificações de risco PCMSO', err);
                this.classificacaoRiscoOptions.set([]);
                this.showToast('error', 'Erro', 'Falha ao carregar classificações de risco');
            }
        });
    }

    selecionarAba(aba: 'ATIVAS' | 'INATIVAS') {
        this.abaSelecionada = aba;
    }

    /**
     * Chave de idempotência da sessão do formulário. O PCMSO em si tem chave do payload, mas a
     * criação grava um histórico por max + 1 — é esse histórico que duplicaria no reenvio.
     */
    private chaveIdempotencia = novaChaveIdempotencia();

    abrirDialogNovo(): void {
        if (this.abaSelecionada !== 'ATIVAS') return;
        this.chaveIdempotencia = novaChaveIdempotencia();
        this.novo();
        this.mostrarFormulario = true;
    }

    abrirDialogRegistro(item: EmpresaCadastrada): void {
        if (item.indiceRegistro == null) return;
        this.indiceAtual = item.indiceRegistro;
        this.renderizarRegistroAtual();
        this.modoCriacao = false;
        this.modoEdicao = false;
        this.removerPdf();
        this.removerSubstituicaoPdf();
        this.mostrarFormulario = true;
        // Aberto a partir de evento dentro da <p-table> (modo tabela): sob OnPush o clique não
        // marca este componente pai para checagem, então força a atualização da view.
        this.cdr.markForCheck();
    }

    onToggleAba(novaAba?: string | { label: string; value: string }): void {
        const valorAba = typeof novaAba === 'object' ? novaAba.value : novaAba;

        // Força a seleção local da aba ao receber o evento para evitar "reversão" visual
        if (valorAba === 'ATIVAS' || valorAba === 'INATIVAS') {
            this.abaSelecionada = valorAba;
        }
        this.first = 0;
        const p = this.pessoa();
        if (p && p.id != null && !!p.uf) this.carregarDados(p);
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? this.rows;
        const p = this.pessoa();
        if (p && p.id != null && !!p.uf) {
            this.carregarDados(p);
        }
    }

    habilitarEdicao(): void {
        // Não permitir edição quando visualizando registros inativos
        if (this.abaSelecionada !== 'ATIVAS') return;
        this.modoEdicao = true;
        this.modoCriacao = false;
        this.pcmsoForm.updateValueAndValidity({ emitEvent: false });
    }

    novo(): void {
        // Não permitir criação quando visualizando registros inativos
        if (this.abaSelecionada !== 'ATIVAS') return;
        this.pcmsoForm.reset();
        this.pcmsoForm.patchValue({ tipoRegistro: 'PJ' });
        this.removerPdf();
        this.removerSubstituicaoPdf();
        this.modoCriacao = true;
        this.modoEdicao = true;
        this.pcmsoForm.updateValueAndValidity({ emitEvent: false });
    }

    salvarDados(): void {
        if (!this.pcmsoForm.valid || !this.pdfValid()) {
            this.pcmsoForm.markAllAsTouched();
            this.pcmsoForm.updateValueAndValidity({ emitEvent: false });
            return;
        }
        this.logger.info('Prosseguindo com o salvamento do PCMSO');

        const pessoa = this.pessoa();
        if (!pessoa || pessoa.id == null || !pessoa.uf) {
            this.erro = 'Pessoa inválida para criação do PCMSO.';
            this.showToast('error', 'Erro', this.erro);
            return;
        }

        const v = this.pcmsoForm.value as {
            cnpj?: string | null;
            razaoSocial?: string;
            emailEmpresa?: string;
            divisaoAreaSetor?: string;
            nrFuncionarios?: number | string | null;
            classificacaoRisco?: number | string | null;
            coordenacaoDataInicio?: string | null;
            coordenacaoDataFim?: string | null;
        };
        if (this.modoCriacao) {
            // Fluxo de criação: exige PDF e envia no campo imDocumento
            if (!this.arquivoPdf) {
                this.showToast('warn', 'Documento obrigatório', 'Selecione um arquivo PDF para enviar.');
                return;
            }

            // Monta o payload base de criação
            const payloadBase = {
                idMedico: Number(pessoa.id),
                uf: String(pessoa.uf),
                // tipoRegistro é informação de UI; não é enviada ao backend
                nuCnpj: this.sanitizeCnpj(v.cnpj),
                nmEmpresa: v.razaoSocial || null,
                nmEmail: v.emailEmpresa || null,
                dsArea: v.divisaoAreaSetor || null,
                qtdFuncionario: v.nrFuncionarios != null && v.nrFuncionarios !== '' ? Number(v.nrFuncionarios) : null,
                idRisco: v.classificacaoRisco != null && v.classificacaoRisco !== '' ? Number(v.classificacaoRisco) : null,
                dtEntrada: this.toIsoDate(v.coordenacaoDataInicio),
                dtSaida: this.toIsoDate(v.coordenacaoDataFim)
            } as const;

            this.loading.set(true);
            this.erro = undefined;

            this.lerArquivoComoBase64(this.arquivoPdf)
                .then((base64) => {
                    const body = { ...payloadBase, imDocumento: base64 } as PcmsoCreateRequest;
                    this.pcmsoFeature.criar(body, comIdempotencia(this.chaveIdempotencia)).subscribe({
                        next: () => {
                            const p = this.pessoa();
                            if (p && p.id != null && !!p.uf) {
                                this.abaSelecionada = 'ATIVAS';
                                this.first = 0;
                                this.carregarDados(p);
                            }
                            this.modoCriacao = false;
                            this.modoEdicao = false;
                            this.arquivoPdf = null;
                            this.arquivoPdfNome = '';
                            // Após concluir o novo cadastro, volta para a página da tabela.
                            this.mostrarFormulario = false;
                            this.showToast('success', 'Sucesso', 'PCMSO criado com sucesso');
                            this.loading.set(false);
                        },
                        error: (err: Error) => {
                            this.logger.error('Erro ao criar PCMSO', err);
                            this.erro = err.message || 'Falha ao criar registro PCMSO.';
                            this.showToast('error', 'Erro', this.erro);
                            this.loading.set(false);
                        }
                    });
                })
                .catch((err) => {
                    this.logger.error('Erro ao ler arquivo PDF', err);
                    this.showToast('error', 'Erro no Anexo', 'Falha ao processar o arquivo PDF.');
                    this.loading.set(false);
                });
        } else {
            // Fluxo de edição: não altera anexo
            const reg = this.registroAtual;
            const nuRegistro = reg?.nuRegistro != null ? Number(reg.nuRegistro) : NaN;
            if (!nuRegistro || Number.isNaN(nuRegistro)) {
                this.showToast('warn', 'Atenção', 'Registro inválido para edição.');
                return;
            }

            const body = {
                nuCnpj: this.sanitizeCnpj(v.cnpj),
                nmEmpresa: v.razaoSocial || null,
                nmEmail: v.emailEmpresa || null,
                dsArea: v.divisaoAreaSetor || null,
                qtdFuncionario: v.nrFuncionarios != null && v.nrFuncionarios !== '' ? Number(v.nrFuncionarios) : null,
                idRisco: v.classificacaoRisco != null && v.classificacaoRisco !== '' ? Number(v.classificacaoRisco) : null,
                dtEntrada: this.toIsoDate(v.coordenacaoDataInicio),
                dtSaida: this.toIsoDate(v.coordenacaoDataFim)
            } as PcmsoUpdateRequest;

            this.loading.set(true);
            this.erro = undefined;
            this.pcmsoFeature.editar({ uf: String(pessoa.uf), idMedico: Number(pessoa.id), nuRegistro }, body).subscribe({
                next: () => {
                    const p = this.pessoa();
                    if (p && p.id != null && !!p.uf) {
                        this.abaSelecionada = 'ATIVAS';
                        this.first = 0;
                        this.carregarDados(p);
                    }
                    this.modoCriacao = false;
                    this.modoEdicao = false;
                    this.showToast('success', 'Sucesso', 'PCMSO atualizado com sucesso');
                    this.loading.set(false);
                },
                error: (err: Error) => {
                    this.logger.error('Erro ao atualizar PCMSO', err);
                    this.erro = err.message || 'Falha ao atualizar registro PCMSO.';
                    this.showToast('error', 'Erro', this.erro);
                    this.loading.set(false);
                }
            });
        }
    }

    cancelarEdicao(): void {
        const possuiRegistros = this.registrosPcmso.length > 0;
        this.pcmsoForm.reset();
        this.pcmsoForm.patchValue({ tipoRegistro: 'PJ' });
        this.modoCriacao = false;
        this.modoEdicao = false;
        this.removerPdf();
        this.removerSubstituicaoPdf();
        this.pcmsoForm.updateValueAndValidity({ emitEvent: false });

        if (possuiRegistros) {
            this.renderizarRegistroAtual();
            return;
        }

        this.mostrarFormulario = false;
    }

    deletar(): void {
        if (confirm('Tem certeza que deseja deletar este registro?')) {
            this.logger.info('Deletando registro PCMSO');
            // Aqui seria implementada a lógica de exclusão
        }
    }

    get empresasExibidas(): EmpresaCadastrada[] {
        return this.abaSelecionada === 'ATIVAS' ? this.empresasAtivas : this.empresasInativas;
    }

    get empresasExibidasTabela(): EmpresaCadastradaTabela[] {
        return this.empresasExibidas.map((item) => ({
            ...item,
            situacao: this.abaSelecionada === 'ATIVAS' ? { value: 'Ativa', color: 'var(--p-green-500)' } : { value: 'Inativa', color: 'var(--p-red-500)' }
        }));
    }

    selecionarRegistro(item: EmpresaCadastrada): void {
        if (this.modoEdicao) return;
        if (item.indiceRegistro == null) return;
        this.indiceAtual = item.indiceRegistro;
        this.renderizarRegistroAtual();
    }

    voltarParaTabela(): void {
        this.modoCriacao = false;
        this.modoEdicao = false;
        this.removerPdf();
        this.removerSubstituicaoPdf();
        this.mostrarFormulario = false;
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor);
        }
    }

    // Navegação e renderização do registro atual
    private renderizarRegistroAtual(): void {
        const registro = this.registrosPcmso[this.indiceAtual];
        if (registro) this.preencherFormularioPcmso(registro);
    }

    // ---- Anexo por registro ----
    get registroAtual(): PcmsoRequest | undefined {
        if (this.modoCriacao) return undefined;
        return this.registrosPcmso[this.indiceAtual];
    }
    visualizarAnexo(): void {
        const p = this.pessoa();
        const reg = this.registroAtual;
        const nuRegistro = reg?.nuRegistro != null ? Number(reg.nuRegistro) : NaN;
        if (!p?.id || !p?.uf || !nuRegistro || Number.isNaN(nuRegistro)) {
            this.showToast('warn', 'Atenção', 'Registro inválido para visualizar anexo');
            return;
        }
        this.pcmsoFeature.downloadAnexo({ uf: p.uf, idMedico: p.id, nuRegistro }).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                window.open(url, '_blank');
                // revogar depois de um tempo
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
            },
            error: (err: Error) => {
                this.showToast('error', 'Erro', err.message || 'Falha ao baixar anexo');
            }
        });
    }

    onSelectSubstituirPdf(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        if (!file) return;
        // Validação tipo e tamanho (padrão Biometria)
        if (file.type !== 'application/pdf') {
            this.showToast('warn', 'Arquivo inválido', 'Selecione um arquivo PDF (.pdf)');
            input.value = '';
            return;
        }
        const max = 10 * 1024 * 1024; // 10MB
        if (file.size > max) {
            this.showToast('warn', 'Arquivo muito grande', 'Tamanho máximo permitido é 10 MB');
            input.value = '';
            return;
        }

        this.substituicaoPdf = file;
        this.substituicaoPdfNome = file.name;
    }

    removerSubstituicaoPdf(): void {
        this.substituicaoPdf = null;
        this.substituicaoPdfNome = '';
    }

    substituirAnexo(pdfInput?: HTMLInputElement | null): void {
        const p = this.pessoa();
        const reg = this.registroAtual;
        const nuRegistro = reg?.nuRegistro != null ? Number(reg.nuRegistro) : NaN;
        if (!p?.id || !p?.uf || !nuRegistro || Number.isNaN(nuRegistro)) {
            this.showToast('warn', 'Atenção', 'Registro inválido para substituir anexo');
            return;
        }
        if (!this.substituicaoPdf) {
            this.showToast('warn', 'Atenção', 'Selecione um PDF para substituir');
            return;
        }

        this.loading.set(true);
        this.pcmsoFeature
            .uploadAnexoRegistro({ uf: p.uf, idMedico: p.id, nuRegistro }, this.substituicaoPdf)
            .subscribe({
                next: () => {
                    this.showToast('success', 'Sucesso', 'Anexo atualizado com sucesso');
                    this.removerSubstituicaoPdf();
                    if (pdfInput) pdfInput.value = '';
                    // Recarrega lista atual
                    this.onToggleAba();
                },
                error: (err: Error) => {
                    this.showToast('error', 'Erro no Anexo', err.message || 'Falha ao atualizar anexo');
                }
            })
            .add(() => {
                this.loading.set(false);
            });
    }

    private preencherFormularioPcmso(r: PcmsoRequest): void {
        if (!this.pcmsoForm) return;
        this.pcmsoForm.patchValue(
            {
                tipoRegistro: r.tipoRegistro ?? this.pcmsoForm.get('tipoRegistro')?.value,
                numeroRegistro: r.controle != null ? String(r.controle) : '',
                cnpj: r.nuCnpj ?? '',
                razaoSocial: r.nmEmpresa ?? '',
                emailEmpresa: (r as { nmEmail?: string }).nmEmail ?? '',
                divisaoAreaSetor: r.dsArea ?? '',
                nrFuncionarios: r.qtdFuncionario ?? '',
                classificacaoRisco: r.idRisco ?? '',
                // Campos de coordenação vindos do model (datas)
                coordenacaoDataInicio: this.formatarData(r.dtEntrada),
                coordenacaoDataFim: this.formatarData(r.dtSaida)
            },
            { emitEvent: false }
        );
    }

    private toIsoDate(valor?: string | null): string | null {
        if (!valor) return null;
        const s = String(valor).trim();
        // Aceita DD/MM/AAAA ou já ISO AAAA-MM-DD
        const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m1) {
            const [, dd, mm, yyyy] = m1;
            return `${yyyy}-${mm}-${dd}`;
        }
        const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m2) return s;
        try {
            const d = new Date(s);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch {
            return null;
        }
    }

    private sanitizeCnpj(v?: string | null): string | null {
        if (!v) return null;
        return String(v).replace(/\D/g, '') || null;
    }

    private limparFormularioSemRegistros(): void {
        if (!this.pcmsoForm) return;
        this.pcmsoForm.reset();
        this.pcmsoForm.patchValue({ tipoRegistro: 'PJ' });
    }

    private showToast(severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string): void {
        this.messageService.add({ severity, summary, detail });
    }

    // ==== Upload de PDF (padrão Biometria) ====
    onSelectPdf(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        // Valida tipo e tamanho (<= 10MB)
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const maxBytes = 10 * 1024 * 1024;
        if (!isPdf) {
            this.showToast('warn', 'Arquivo inválido', 'Selecione um arquivo em formato PDF (.pdf).');
            (event.target as HTMLInputElement).value = '';
            return;
        }
        if (file.size > maxBytes) {
            this.showToast('warn', 'Arquivo muito grande', 'O PDF deve ter no máximo 10 MB.');
            (event.target as HTMLInputElement).value = '';
            return;
        }
        this.arquivoPdf = file;
        this.arquivoPdfNome = file.name;
        this.showToast('info', 'Anexo selecionado', `${file.name}`);
    }

    removerPdf(): void {
        this.arquivoPdf = null;
        this.arquivoPdfNome = '';
    }

    // Utilitário: converte arquivo para base64 (sem prefixo data:)
    private lerArquivoComoBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const res = reader.result;
                if (typeof res === 'string') {
                    const commaIdx = res.indexOf(',');
                    const base64 = commaIdx >= 0 ? res.substring(commaIdx + 1) : res;
                    resolve(base64);
                } else if (res instanceof ArrayBuffer) {
                    // Fallback caso use readAsArrayBuffer (não utilizado aqui)
                    const bytes = new Uint8Array(res);
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                    resolve(btoa(binary));
                } else {
                    reject(new Error('Formato de leitura não suportado'));
                }
            };
            reader.onerror = () => reject(reader.error || new Error('Erro ao ler arquivo'));
            reader.readAsDataURL(file);
        });
    }

    private pdfValid() {
        if (!this.modoCriacao) return true;
        return this.arquivoPdf != null && this.arquivoPdf.size > 0;
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.pcmsoForm?.get(nomeCampo);
        const campoPeriodoCoordenacao = nomeCampo === 'coordenacaoDataInicio' || nomeCampo === 'coordenacaoDataFim';
        const periodoInvalido = campoPeriodoCoordenacao && !!this.pcmsoForm?.hasError('coordenacaoPeriodoInvalido');

        return !!campo && (campo.invalid || periodoInvalido) && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.pcmsoForm?.get(nomeCampo);
        const campoPeriodoCoordenacao = nomeCampo === 'coordenacaoDataInicio' || nomeCampo === 'coordenacaoDataFim';

        if (campoPeriodoCoordenacao && this.pcmsoForm?.hasError('coordenacaoPeriodoInvalido')) {
            return 'A data de início da coordenação não pode ser superior à data fim.';
        }

        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['cnpjInvalido']) return 'CNPJ inválido.';
        if (campo.errors['email'] || campo.errors['emailInvalido']) return 'E-mail inválido.';
        if (campo.errors['dataInvalida']) return 'Data inválida.';
        if (campo.errors['dataMaiorQueHojeInvalida']) return 'A data deve ser menor ou igual a hoje.';
        if (campo.errors['min']) return 'O número de funcionários não pode ser negativo.';

        return 'Valor inválido.';
    }

    exibirErroPdfCriacao(): boolean {
        return this.modoCriacao && this.pcmsoForm?.hasError('invalidPdf') && this.pcmsoForm.touched;
    }
}
